/**
 * Toast Component - Notification toasts for YMS
 *
 * Matches Xamarin ToastMessageDialog styling:
 *  - Positioned at bottom center (LayoutBounds="0.5,0.98,-1,-1")
 *  - Width ~75% of screen
 *  - Auto-dismiss after 3 seconds
 *  - Colored backgrounds per type:
 *    - success: #5ed244 (green) + checkmark icon
 *    - error:   #FFA319 (orange) + warning icon
 *    - warning: #Ff4a4a (red) + error/alert icon
 *  - White text, PoppinsRegular font
 *  - Horizontal layout: icon (20px) + label, centered, spacing 10
 *  - Frame Padding=20 (phone=10), no shadow
 */

import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { cn } from '@/utils/cn';

// Toast type colors matching Xamarin:
// 0 (success): #5ed244 green + CheckCircle
// 1 (warning): #FFA319 orange + AlertTriangle
// 2 (error):   #Ff4a4a red + XCircle
// default:     rgba(0,0,0,0.9) black
const toastConfig = {
  success: { bg: 'bg-[#5ed244]', Icon: CheckCircle },
  warning: { bg: 'bg-[#FFA319]', Icon: AlertTriangle },
  error: { bg: 'bg-[#Ff4a4a]', Icon: XCircle },
  info: { bg: 'bg-black/90', Icon: null },
} as const;

export interface ToastProps {
  id: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onDismiss: (id: string) => void;
}

export function Toast({ id, message, type = 'success', duration = 3000, onDismiss }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const { bg, Icon } = toastConfig[type];

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    // Wait for exit animation before removing
    setTimeout(() => {
      onDismiss(id);
    }, 200);
  }, [onDismiss, id]);

  // Animate in on mount
  useEffect(() => {
    // Small delay to trigger animation
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 10);
    return () => {
      clearTimeout(showTimer);
    };
  }, []);

  // Auto-dismiss after duration
  useEffect(() => {
    const dismissTimer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => {
      clearTimeout(dismissTimer);
    };
  }, [duration, handleDismiss]);

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-5 rounded-xl shadow-lg max-w-[75vw]',
        bg,
        'transform transition-all duration-200 ease-out',
        isVisible && !isExiting ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      )}
      role="alert"
      aria-live="polite"
    >
      {Icon && <Icon className="h-5 w-5 text-white flex-shrink-0" />}
      <span className="text-white text-[14px] font-medium">{message}</span>
    </div>
  );
}

Toast.displayName = 'Toast';
